export interface SiteModerationData {
  blockedUsers: string[];
  updatedAt?: string;
}

export type SkyspaceActivityKind =
  | 'login'
  | 'profile'
  | 'appearance'
  | 'music'
  | 'interests'
  | 'top8'
  | 'post'
  | 'comment'
  | 'reply';

export interface RecentSkyspacePerson {
  did: string;
  handle: string;
  firstSeenAt: string;
  lastActiveAt: string;
  discoveryAt: string;
  lastActivityKind: SkyspaceActivityKind;
}

export async function getRecentSkyspacePeople(
  signal?: AbortSignal,
): Promise<RecentSkyspacePerson[]> {
  const response = await fetch('../recent-people.php', {
    signal,
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return [];
  const value = await response.json() as { people?: RecentSkyspacePerson[] };
  return Array.isArray(value.people) ? value.people : [];
}

export function recordSkyspaceActivity(
  did: string | undefined,
  handle: string | undefined,
  activity: SkyspaceActivityKind,
): void {
  if (!did || !handle || typeof window === 'undefined') return;
  void fetch('../recent-people.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ did, handle, activity }),
    keepalive: true,
  }).catch(() => undefined);
}

export async function getSiteModeration(signal?: AbortSignal): Promise<SiteModerationData> {
  const response = await fetch('../moderation-data.php', {
    signal,
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return { blockedUsers: [] };
  const value = await response.json() as Partial<SiteModerationData>;
  return {
    blockedUsers: Array.isArray(value.blockedUsers)
      ? value.blockedUsers.filter(item => typeof item === 'string').map(item => item.toLowerCase())
      : [],
    updatedAt: value.updatedAt,
  };
}

const ANALYTICS_EVENTS = new Set([
  'page_view',
  'profile_view',
  'search',
  'settings_open',
  'settings_sensitive_enabled',
  'music_preview',
  'interests_saved',
  'top8_saved',
  'moderation_hidden',
  'chat_message',
]);

export function normalizeReferralHostname(
  currentUrl: string,
  referrerUrl: string,
): string {
  if (referrerUrl.trim() === '') return 'direct';
  try {
    const current = new URL(currentUrl);
    const referrer = new URL(referrerUrl);
    if (!['http:', 'https:'].includes(referrer.protocol)) return 'direct';
    if (current.hostname === referrer.hostname) return 'internal';
    return referrer.hostname.toLowerCase().replace(/^www\./, '').slice(0, 253);
  } catch {
    return 'direct';
  }
}

let referralRecordedForDocument = false;

export function referralForNextPageView(): string | undefined {
  if (typeof window === 'undefined' || referralRecordedForDocument) return undefined;
  referralRecordedForDocument = true;
  return normalizeReferralHostname(window.location.href, document.referrer);
}

export function trackSiteEvent(
  event: string,
  dimensions: { profile?: string; provider?: string; referrer?: string } = {},
): void {
  if (!ANALYTICS_EVENTS.has(event) || typeof window === 'undefined') return;
  void fetch('../analytics.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event, dimensions }),
    keepalive: true,
  }).catch(() => undefined);
}
